/**
 * PixelInspector — headless effect that turns a map-canvas pick into a
 * `RasterPixelSample` and writes it to
 * `useViewerStore.rasterToolCard.lastSample`.
 *
 * Background (WP-3 refactor): the previous version painted a floating
 * dark-mode popup with a live runtime-computed colour swatch and a fixed
 * dark background, both of which violated the technical-canvas-stays-
 * light rule (`UIUX_DETAILED_SURFACE_STUDY.md` §3) and the design-bridge
 * raw-constant rule. The sampled pixel now lives in the store; the
 * swatch (rendered from a tokenized hex string via `channelTripleToHex`)
 * and the three-branch presentation (`ok` / `nodata` / `error`) are
 * owned by the Raster ToolCard pick row
 * (`components/raster/RasterPixelPickRow.tsx`).
 *
 * The component still owns:
 *   - the `/cog/point` HTTP request,
 *   - cursor style management while pick mode is active,
 *   - mapping the response (or HTTP failure) into the three branches.
 */

import { useEffect } from 'react';
import { usePluginStore } from '@/state/usePluginStore';
import {
  useViewerStore,
  type RasterPixelSample,
} from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';

const TILES_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TILES_URL) ||
  'http://localhost:8000'
).replace(/\/+$/, '');

export interface PixelInspectorProps {
  container: HTMLElement | null;
  cogUrl: string | null;
  /** Optional fetch hook for tests; defaults to `globalThis.fetch`. */
  fetcher?: typeof fetch;
}

/**
 * Pure mapper used by the inspector AND by `__tests__` to assert
 * branching without going through `fetch`. Exported so tests can call it
 * directly with synthetic TiTiler responses.
 */
export function buildPixelSample(input: {
  lon: number;
  lat: number;
  payload: { values?: number[]; band_names?: string[] } | null;
  httpOk: boolean;
  errorMessage?: string;
}): RasterPixelSample {
  const { lon, lat, payload, httpOk, errorMessage } = input;
  if (!httpOk || !payload) {
    return {
      lon,
      lat,
      values: [],
      bandNames: [],
      state: 'error',
      errorMessage: errorMessage ?? 'request failed',
    };
  }
  const values = payload.values ?? [];
  const bandNames =
    payload.band_names ?? values.map((_, i) => `b${i + 1}`);
  if (values.length === 0 || values.every((v) => !Number.isFinite(v))) {
    return { lon, lat, values, bandNames, state: 'nodata' };
  }
  return { lon, lat, values, bandNames, state: 'ok' };
}

export function PixelInspector({
  container,
  cogUrl,
  fetcher,
}: PixelInspectorProps) {
  const activePlugin = usePluginStore((s) => s.activePluginId);
  const selection = useViewerSlice((s) => s.selection);
  const setRasterToolCard = useViewerStore((s) => s.setRasterToolCard);

  const enabled = activePlugin === null && !!cogUrl;

  useEffect(() => {
    if (!enabled) return;
    if (!selection?.point || selection.point.length < 2) return;
    const [lon, lat] = selection.point;
    if (typeof lon !== 'number' || typeof lat !== 'number') return;

    let cancelled = false;
    const doFetch = fetcher ?? globalThis.fetch.bind(globalThis);

    void (async () => {
      try {
        const url = `${TILES_BASE}/cog/point/${lon},${lat}?url=${encodeURIComponent(cogUrl!)}`;
        const res = await doFetch(url);
        if (cancelled) return;
        if (!res.ok) {
          setRasterToolCard({
            lastSample: buildPixelSample({
              lon,
              lat,
              payload: null,
              httpOk: false,
              errorMessage: `HTTP ${res.status}`,
            }),
          });
          return;
        }
        const data = (await res.json()) as {
          coordinates?: [number, number];
          values?: number[];
          band_names?: string[];
        };
        if (cancelled) return;
        setRasterToolCard({
          lastSample: buildPixelSample({
            lon,
            lat,
            payload: data,
            httpOk: true,
          }),
        });
      } catch (err) {
        if (cancelled) return;
        setRasterToolCard({
          lastSample: buildPixelSample({
            lon,
            lat,
            payload: null,
            httpOk: false,
            errorMessage:
              err instanceof Error ? err.message : 'network error',
          }),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, selection, cogUrl, fetcher, setRasterToolCard]);

  // Set pointer cursor when pixel inspect is active (no plugin active).
  useEffect(() => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;
    if (enabled) {
      map.getCanvas().style.cursor = 'pointer';
    }
    return () => {
      const canvas = map.getCanvas();
      if (canvas) canvas.style.cursor = '';
    };
  }, [container, enabled]);

  return null;
}
